import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingRow } from "../../components/template/row/LoadingRow";
import { RowHeader } from "../../components/template/row/RowHeader";
import { TemplateRow } from "../../components/template/row/TemplateRow";
import { PagePath } from "../../navigation/routes";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { clearChildTemplates } from "../../store/ticketGrids/ticketGridsSlice";
import { getTicketGridChildTemplates } from "../../store/ticketGrids/thunks/getTicketGridChildTemplates";
import { getTicketGridList } from "../../store/ticketGrids/thunks/getTicketGridList";
import { TicketGridTemplateCard } from "./templates/TicketGridTemplateCard";

const childColSpan = 6;

export const TicketGridTemplates = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { parentId: parentIdParam } = useParams();
  const templates = useAppSelector((s) => s.ticketGrids.templates ?? []);
  const loadingReports = useAppSelector((s) => s.ticketGrids.loadingReports);
  const parentId = useAppSelector((s) => s.ticketGrids.parentId);
  const childTemplates = useAppSelector((s) => s.ticketGrids.childTemplates ?? []);
  const loadingChildTemplates = useAppSelector(
    (s) => s.ticketGrids.loadingChildTemplates,
  );
  const isLoggedIn = useAppSelector(
    (s) => s.user.isLoggedIn && s.user.info !== null,
  );
  const shouldRefreshList = useAppSelector(
    (s) => s.ticketGrids.ticketGridsCreating === false && s.ticketGrids.templates.length > 0,
    (l, r) => l === r,
  );

  const showChildTemplates = parentId !== undefined;

  useEffect(() => {
    if (isLoggedIn) {
      const token = dispatch(getTicketGridList());
      return () => token.abort();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (shouldRefreshList) {
      const token = dispatch(getTicketGridList());
      return () => token.abort();
    }
  }, [shouldRefreshList]);

  useEffect(() => {
    if (parentIdParam) {
      const parentIdFromUrl = parseInt(parentIdParam, 10);
      if (!Number.isNaN(parentIdFromUrl)) {
        const token = dispatch(getTicketGridChildTemplates(parentIdFromUrl));
        return () => token.abort();
      }
    } else {
      dispatch(clearChildTemplates());
    }
  }, [parentIdParam]);

  const goToTicketGridList = () => {
    navigate(PagePath.ticketGrids.templates);
  };

  return (
    <Grid container spacing={1} rowSpacing={2} alignItems="center">
      {showChildTemplates ? (
        <Grid item xs={12}>
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={goToTicketGridList}
              >
                К списку билетных сеток
              </Button>
            }
          >
            Шаблоны билетной сетки ID: {parentId}. По каждому оператору проверьте
            дополнительные фильтры и сохраните.
          </Alert>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        {showChildTemplates ? (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="Шаблоны билетной сетки">
              <RowHeader mode={"templates"} isJobs={false} />
              <TableBody>
                {loadingChildTemplates || childTemplates.length === 0 ? (
                  <LoadingRow
                    colSpan={childColSpan}
                    loading={loadingChildTemplates}
                  />
                ) : null}
                {childTemplates.map((job, index) => (
                  <TemplateRow
                    key={`ticketGridChild${job.id}.${index}`}
                    job={job}
                    mode={"templates"}
                    colSpan={childColSpan}
                    isJobs={false}
                    hideClone={true}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : loadingReports ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress color="inherit" />
          </Box>
        ) : templates.length === 0 ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Нет подходящих результатов</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} justifyContent="center" aria-label="Билетные сетки">
            {templates.map((info) => (
              <Grid item key={`${info.id}`} xs={12} sm={6} md={3}>
                <TicketGridTemplateCard info={info} />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
